import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { CalendarClock, Play } from "lucide-react";
import { SmsStatusBadge } from "./SmsStatusBadge";

const FILTERS = ["pending", "", "test_mode", "sent", "failed", "skipped", "cancelled", "missed"];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Brisbane", weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "");

export default function SmsReminderQueue({ refreshKey, onRan }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const [running, setRunning] = useState(false);

  const load = useCallback(() => {
    api.get("/sms/reminders", { params: { status, limit: 300 } }).then((r) => {
      setRows(r.data);
    }).catch(() => {});
  }, [status]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/sms/reminders/run");
      toast.success(data.skipped ? "A check is already running" : `Checked · ${data.created} new, ${data.cancelled} cancelled, ${data.processed.length} due`);
      load(); onRan?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setRunning(false); }
  };

  return (
    <section className="border border-border/60 bg-card p-4 sm:p-8 rounded-sm" data-testid="sms-reminder-queue">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 font-medium"><CalendarClock className="w-4 h-4 text-brand" /> Reminder schedule</div>
          <p className="text-xs text-muted-foreground mt-1">One reminder per booking time — it can only ever be sent once. Times in Australia/Brisbane.</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="sms-queue-filter"
            className="border border-border/70 rounded-sm px-3 py-2 bg-background text-sm outline-none focus:border-brand">
            {FILTERS.map((f) => <option key={f || "all"} value={f}>{f === "pending" ? "Scheduled" : f ? f.replace("_", " ") : "All"}</option>)}
          </select>
          <Button variant="outline" onClick={runNow} disabled={running} data-testid="sms-queue-run" className="rounded-sm gap-2">
            <Play className="w-4 h-4" /> {running ? "Checking…" : "Check now"}
          </Button>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="sms-queue-empty">Nothing here.</p>
      ) : (
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Client</TableHead><TableHead>Appointment</TableHead><TableHead>Reminder due</TableHead><TableHead>Status</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`sms-queue-row-${r.id}`}>
                  <TableCell className="text-sm whitespace-nowrap">{r.customer_name || "—"}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">
                    <Link to={`/jobs?job=${r.job_id}`} className="text-brand hover:underline">{fmt(r.appointment_at)}</Link>
                    {r.rescheduled && <div className="text-xs text-muted-foreground">Rescheduled</div>}
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{fmt(r.due_at)}</TableCell>
                  <TableCell><SmsStatusBadge status={r.status} />{r.reason && <div className="text-xs text-muted-foreground mt-1 max-w-[260px]">{r.reason}</div>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}
